import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Tabs,
    Tab,
    TextField,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    Button,
    useTheme,
    Stack,
    FormControl,
    InputLabel,
    Checkbox,
    Divider,
    Snackbar,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { dataElementsService, DataElement, CreateDataElement } from '../../services/dataelements-service';
import { config } from '../../config/config';

const DATA_ELEMENT_TYPES = [
    'Text', 'Date', 'Selection', 'Email', 'Phone', 'Country', 'State', 'Pan', 'Pin', 'Aadhar', 'Number'
];

// List of 23 DPDP Act languages (example codes and labels)
const DPDP_LANGUAGES: { code: string; label: string }[] = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'Hindi' },
    { code: 'bn', label: 'Bengali' },
    { code: 'te', label: 'Telugu' },
    { code: 'mr', label: 'Marathi' },
    { code: 'ta', label: 'Tamil' },
    { code: 'gu', label: 'Gujarati' },
    { code: 'ur', label: 'Urdu' },
    { code: 'kn', label: 'Kannada' },
    { code: 'or', label: 'Odia' },
    { code: 'ml', label: 'Malayalam' },
    { code: 'pa', label: 'Punjabi' },
    { code: 'as', label: 'Assamese' },
    { code: 'ma', label: 'Maithili' },
    { code: 'sa', label: 'Sanskrit' },
    { code: 'sd', label: 'Sindhi' },
    { code: 'ks', label: 'Kashmiri' },
    { code: 'ne', label: 'Nepali' },
    { code: 'ko', label: 'Konkani' },
    { code: 'do', label: 'Dogri' },
    { code: 'mt', label: 'Manipuri' },
    { code: 'bo', label: 'Bodo' },
    { code: 'sa', label: 'Santhali' },
];

interface AddDataElementPanelProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const AddDataElementPanel: React.FC<AddDataElementPanelProps> = ({ open, onClose, onSave }) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState('Text');
    const [isIdentifier, setIsIdentifier] = useState(false);
    const [isPersonalData, setIsPersonalData] = useState(false);
    const [isMultiSelect, setIsMultiSelect] = useState(false);
    const [validationRule, setValidationRule] = useState('');
    const [codeError, setCodeError] = useState('');
    const [nameError, setNameError] = useState('');
    const [typeError, setTypeError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName('');
            setCode('');
            setDescription('');
            setType('Text');
            setIsIdentifier(false);
            setIsPersonalData(false);
            setIsMultiSelect(false);
            setValidationRule('');
            setCodeError('');
            setNameError('');
            setTypeError('');
            setSaveError('');
        }
    }, [open]);

    const validateForm = () => {
        let isValid = true;

        // Validate name
        if (!name.trim()) {
            setNameError('Name is required');
            isValid = false;
        } else if (name.trim().length < 3) {
            setNameError('Name must be at least 3 characters long');
            isValid = false;
        } else {
            setNameError('');
        }

        // Validate code
        if (!code.trim()) {
            setCodeError('Code is required');
            isValid = false;
        } else if (!/^[a-zA-Z0-9_-]+$/.test(code.trim())) {
            setCodeError('Code can only contain letters, numbers, underscores, and hyphens');
            isValid = false;
        } else {
            setCodeError('');
        }

        // Validate type
        if (!type) {
            setTypeError('Type is required');
            isValid = false;
        } else {
            setTypeError('');
        }

        return isValid;
    };

    const handleSave = async () => {
        setSaveError('');
        setError(null);
        setIsSaving(true);

        try {
            // Validate element code format
            const valueRegex = /^[a-zA-Z0-9_-]+$/;
            if (!valueRegex.test(code.trim())) {
                setError('Element code can only contain letters, numbers, underscores, and hyphens');
                setIsSaving(false);
                return;
            }

            const newElement: CreateDataElement = {
                clientId: 1, // This should come from your auth context or config
                elementName: name.trim(),
                elementCode: code.trim(),
                description: description.trim(),
                typeId: DATA_ELEMENT_TYPES.indexOf(type) + 1,
                isIdentifier: isIdentifier,
                isPersonalData: isPersonalData,
                isMultiSelect: isMultiSelect,
                validationRule: validationRule.trim() || undefined,
                options: [],
                isPublished: true,
                isActive: true
            };

            await dataElementsService.createDataElement(newElement);
            onSave?.();
            onClose();
        } catch (error: any) {
            console.error('Error saving data element:', error);
            setError(error.response?.data?.message || 'Failed to save data element');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '600px',
                    bgcolor: theme.palette.background.paper,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Add Data Element
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Tabs
                        value={currentTab}
                        onChange={(_, v) => setCurrentTab(v)}
                        sx={{
                            '& .MuiTab-root': {
                                color: theme.palette.text.secondary,
                                '&.Mui-selected': {
                                    color: theme.palette.primary.main,
                                },
                            },
                        }}
                    >
                        <Tab label="Details" />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {currentTab === 0 && (
                        <Box sx={{ p: 3 }}>
                            <Stack spacing={3}>
                                <TextField 
                                    label="Name" 
                                    fullWidth 
                                    size="small" 
                                    required 
                                    value={name} 
                                    onChange={e => setName(e.target.value)}
                                    error={!!nameError}
                                    helperText={nameError}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TextField label="Code" fullWidth size="small" required value={code} onChange={e => setCode(e.target.value)} error={!!codeError} helperText={codeError} />
                                    <Tooltip title="Specify a unique code for this data element">
                                        <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.neutral.main, cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <TextField label="Description" fullWidth size="small" multiline rows={3} value={description} onChange={e => setDescription(e.target.value)} />
                                <FormControl fullWidth size="small">
                                    <InputLabel id="type-label">Type</InputLabel>
                                    <Select
                                        labelId="type-label"
                                        label="Type"
                                        value={type}
                                        onChange={e => setType(e.target.value)}
                                    >
                                        {DATA_ELEMENT_TYPES.map(opt => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {type === 'Selection' && (
                                    <>
                                        <FormControlLabel
                                            control={<Switch checked={isMultiSelect} onChange={e => setIsMultiSelect(e.target.checked)} />}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    Is MultiSelect
                                                    <Tooltip title="Allows multiple values to be selected for this data element if enabled.">
                                                        <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.neutral.main, cursor: 'pointer' }} />
                                                    </Tooltip>
                                                </Box>
                                            }
                                        />
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mt: -2, mb: 2 }}>
                                            Selection options can be added after creating this data element.
                                        </Typography>
                                    </>
                                )}
                                <FormControlLabel
                                    control={<Switch checked={isIdentifier} onChange={e => setIsIdentifier(e.target.checked)} />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Is Identifier
                                            <Tooltip title="Marks this data element as a unique identifier (e.g., Employee ID, Email, etc.)">
                                                <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.neutral.main, cursor: 'pointer' }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    control={<Switch checked={isPersonalData} onChange={e => setIsPersonalData(e.target.checked)} />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Is Personal Data
                                            <Tooltip title="Indicates if this data element contains personal or sensitive information (PII/PHI)">
                                                <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.neutral.main, cursor: 'pointer' }} />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                                <TextField label="Validation Rule (Regex)" fullWidth size="small" value={validationRule} onChange={e => setValidationRule(e.target.value)} />
                            </Stack>
                        </Box>
                    )}
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }}>
                    {saveError && (
                        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                            {saveError}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={onClose} sx={{ color: theme.palette.text.primary }}>
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleSave}
                            sx={{ 
                                bgcolor: theme.palette.primary.main, 
                                color: theme.palette.primary.contrastText, 
                                '&:hover': { bgcolor: theme.palette.primary.dark } 
                            }}
                            disabled={isSaving}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Add Snackbar for error messages */}
            <Snackbar 
                open={!!error} 
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={() => setError(null)}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Drawer>
    );
};

export default AddDataElementPanel; 